import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { put } from '@nineeng/request';
import { DetailsTab, Spinner } from '@repo/components';
import { getGroup } from 'src/api/personal/groups';
import { getGroupForm } from 'src/api/personal/groupDetail';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission } from '@repo/shared/helpers';
import { EventEmitter } from '@repo/shared/helpers';
import { constructQuery } from '@repo/shared/helpers';
import { PERMISSIONS } from '@repo/shared/domain';

// If addressLine1 is locked by companyData, lock addressLine2 too (same registered address block)
function getReadOnlyFields(companyData: Record<string, unknown> | undefined | null): string[] {
	const fields = Object.keys(companyData || {}).filter(
		(key) => companyData?.[key] != null && companyData?.[key] !== '',
	);
	if (fields.includes('addressLine1') && !fields.includes('addressLine2')) {
		fields.push('addressLine2');
	}
	return fields;
}

export const Route = createFileRoute('/personal_/groups/$groupId/details')({
	component: GroupDetailsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		lang: (search.lang as string) || 'en',
	}),
});

function GroupDetailsPage() {
	const { groupId } = Route.useParams();
	const { lang } = Route.useSearch();
	const queryClient = useQueryClient();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const { data: group, isLoading: groupLoading } = useQuery({
		queryKey: ['personal-group', groupId],
		queryFn: () => getGroup(groupId),
	});

	const { data: groupForm, isLoading: formLoading } = useQuery({
		queryKey: ['personal-group-form', group?.type?.Form?.slug, lang],
		queryFn: () => getGroupForm(group?.type?.Form?.slug || '', constructQuery({ lang })),
		enabled: !!group?.type?.Form?.slug,
	});

	if (userLoading || groupLoading || formLoading) {
		return (
			<div className="flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!group) return null;

	const canEdit = verifyPermission([PERMISSIONS.personalGroupsEdit]);

	const handleDetailsUpdate = async (eventData: Record<string, unknown>) => {
		try {
			await put(`/api/personal-dashboard/groups/${groupId}/details`, { ...eventData });
			EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
			queryClient.invalidateQueries({ queryKey: ['personal-group', groupId] });
		} catch {
			EventEmitter.emit('toast', { type: 'error', message: 'Error saving' });
		}
	};

	const printHeader = (
		<div className="flex gap-2 p-4">
			<div className="w-[300px] flex items-center">
				<img style={{ objectFit: 'contain', maxHeight: 70, width: '100%' }} src="/api/public/logo" alt="Logo" />
			</div>
			<div className="flex-grow p-2">
				<p className="font-bold">Company</p>
				<p>{group?.name || '-'}</p>
			</div>
		</div>
	);

	return (
		<div className="px-4 pt-4">
		<DetailsTab
			data={{ ...group?.groupData, ...group?.companyData }}
			printHeader={printHeader}
			form={groupForm}
			onSave={handleDetailsUpdate}
			readOnly={!canEdit}
			readOnlyFields={getReadOnlyFields(group?.companyData)}
			language={lang}
			onLanguageChange={() => {}}
			availableLanguages={groupForm?.availableLanguages ?? [lang]}
		/>
		</div>
	);
}
